import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import moment from "moment";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";

import MenuIcon from "@material-ui/icons/Menu";
import Tooltip from "@material-ui/core/Tooltip";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import CachedIcon from "@material-ui/icons/Cached";
import DeleteSweepIcon from "@material-ui/icons/DeleteSweep";
import EventNoteIcon from "@material-ui/icons/EventNote";

import 'howler';
import MainListItems from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import NotificationsVolume from "../components/NotificationsVolume";
import UserModal from "../components/UserModal";
import ConfirmationModal from "../components/ConfirmationModal";
import SessionStatusIndicator from "../components/SessionStatusIndicator";
import ConnectionAlerts from "../components/ConnectionAlerts";
import CallPanel from "../components/CallPanel";
import DebugMenu from "../components/DebugMenu";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import DarkMode from "../components/DarkMode";
import LanguageMenu from "../components/LanguageMenu";
import { i18n } from "../translate/i18n";
import toastError from "../errors/toastError";
import AnnouncementsPopover from "../components/AnnouncementsPopover";

import logo from "../assets/logo.png";
import { SocketContext } from "../context/Socket/SocketContext";
import ChatPopover from "../pages/Chat/ChatPopover";

import { useDate } from "../hooks/useDate";

import ColorModeContext from "../layout/themeContext";
import Brightness4Icon from '@material-ui/icons/Brightness4';
import Brightness7Icon from '@material-ui/icons/Brightness7';
import { openFollowupDashboardPopup } from "../helpers/followUpPrompt";
import useSidebarSettings, { getCompanyId } from "../hooks/useSidebarSettings";
import { clearSessionKeys } from "../helpers/sessionStorageKeys";
import { clearAppData, CLEAR_APP_DATA_CHANNEL } from "../helpers/clearAppData";
import { setLicenseNotice } from "../helpers/licenseNotice";
import useNotificationPermission from "../hooks/useNotificationPermission";

const drawerWidth = 240;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "calc(100vh - 56px)",
    },
    backgroundColor: theme.palette.fancyBackground,
    '& .MuiButton-outlinedPrimary': {
      color: theme.mode === 'light' ? '#FFF' : '#FFF',
	  //backgroundColor: theme.mode === 'light' ? '#682ee2' : '#682ee2',
	backgroundColor: theme.mode === 'light' ? theme.palette.primary.main : '#1c1c1c',
      //border: theme.mode === 'light' ? '1px solid rgba(0 124 102)' : '1px solid rgba(255, 255, 255, 0.5)',
    },
    '& .MuiButton-outlinedPrimary:hover': {
      color: theme.mode === 'light' ? theme.palette.primary.main : '#FFF',
    },
    '& .MuiTab-root:hover': {
      color: theme.mode === 'light' ? theme.palette.primary.main : '#FFF',
    },
    '& .MuiTab-textColorPrimary.Mui-selected': {
      color: theme.mode === 'light' ? 'Primary' : '#FFF',
    }
  },
  avatar: {
    width: "100%",
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
    color: theme.palette.dark.main,
    background: theme.palette.barraSuperior,
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
    [theme.breakpoints.down("sm")]: {
      height: "48px"
    },
    "& .MuiIconButton-root": {
      minWidth: 40,
      minHeight: 40,
    }
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginLeft: 0,
      width: "100%",
    },
    [theme.breakpoints.down("md")]: {
      marginLeft: 0,
      width: "100%",
    }
  },
  menuButton: {
    marginRight: 36,
    [theme.breakpoints.down("md")]: {
      marginRight: 16,
    },
  },
  menuButtonHidden: {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "inline-flex", // Sempre mostrar em telas médias e pequenas
    },
  },
  title: {
    flexGrow: 1,
    fontSize: 14,
    color: "white",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [theme.breakpoints.down("md")]: {
      width: 280,
      maxWidth: "85vw",
    },
    ...theme.scrollbarStylesSoft
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    // 8 (padding da lista) + 16 (padding do item) + 24 (icone) + 16 + 8, o que
    // centraliza o icone. O texto e escondido por menuCollapsed, nao pela
    // largura da barra. A conta so fecha porque containerWithScroll usa
    // overflowY: auto — com "scroll" a calha comeria 8px desse total.
    width: theme.spacing(7),
    [theme.breakpoints.up("sm")]: {
      width: theme.spacing(9),
    },
    [theme.breakpoints.down("md")]: {
      width: 0,
      maxWidth: 0,
    }
  },
  drawerVersion: {
    flexShrink: 0,
    fontSize: "12px",
    padding: "10px",
    textAlign: "right",
    fontWeight: "bold",
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    overflow: "auto",

  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column"
  },
  containerWithScroll: {
    flex: 1,
    padding: theme.spacing(1),
    // "auto" e nao "scroll": com "scroll" o navegador reserva os 8px da calha
    // sempre, mesmo sem conteudo para rolar, e essa sobra descentraliza os
    // icones da barra retraida (ver drawerPaperClose).
    overflowY: "auto",
    // O drawerPaper usa whiteSpace: nowrap, entao um rotulo longo geraria barra
    // horizontal com o menu aberto (retraido o drawerPaperClose ja corta).
    overflowX: "hidden",
    ...theme.scrollbarStyles,
  },
  NotificationsPopOver: {
    // color: theme.barraSuperior.secondary.main,
  },
  logo: {
    width: "80%",
    height: "auto",
    maxWidth: 180,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      height: "80%",
      maxWidth: 180,
    },
    logo: theme.logo
  },
}));

const isMobileWidth = () => document.body.offsetWidth < 960;

const userPrefKey = (companyId) => `sidebarUserPref:${companyId}`;

const LoggedInLayout = ({ children, themeToggle }) => {
  const classes = useStyles();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [clearCacheModalOpen, setClearCacheModalOpen] = useState(false);
  const [clearingCache, setClearingCache] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const { handleLogout, loading, isAuth } = useContext(AuthContext);
  const { sidebarBehavior, sidebarSubmenuBehavior } = useSidebarSettings();
  const companyId = getCompanyId();

  const [drawerOpen, setDrawerOpen] = useState(() => {
    if (isMobileWidth()) return false;
    const pref = localStorage.getItem(userPrefKey(companyId));
    if (pref) return pref === "open";
    return sidebarBehavior === "expanded";
  });
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  // const [dueDate, setDueDate] = useState("");
  const { user } = useContext(AuthContext);

  const theme = useTheme();
  const { colorMode } = useContext(ColorModeContext);
  const greaterThenSm = useMediaQuery(theme.breakpoints.up("sm"));

  const [volume, setVolume] = useState(localStorage.getItem("volume") || 1);

  // Pede a permissão de notificação no primeiro clique/tecla dentro do app.
  useNotificationPermission();

  const { dateToClient } = useDate();


  //################### CODIGOS DE TESTE #########################################
  // useEffect(() => {
  //   navigator.getBattery().then((battery) => {
  //     console.log(`Battery Charging: ${battery.charging}`);
  //     console.log(`Battery Level: ${battery.level * 100}%`);
  //     console.log(`Charging Time: ${battery.chargingTime}`);
  //     console.log(`Discharging Time: ${battery.dischargingTime}`);
  //   })
  // }, []);

  // useEffect(() => {
  //   const geoLocation = navigator.geolocation

  //   geoLocation.getCurrentPosition((position) => {
  //     let lat = position.coords.latitude;
  //     let long = position.coords.longitude;

  //     console.log('latitude: ', lat)
  //     console.log('longitude: ', long)
  //   })
  // }, []);

  // useEffect(() => {
  //   const nucleos = window.navigator.hardwareConcurrency;

  //   console.log('Nucleos: ', nucleos)
  // }, []);

  // useEffect(() => {
  //   console.log('userAgent', navigator.userAgent)
  //   if (
  //     navigator.userAgent.match(/Android/i)
  //     || navigator.userAgent.match(/webOS/i)
  //     || navigator.userAgent.match(/iPhone/i)
  //     || navigator.userAgent.match(/iPad/i)
  //     || navigator.userAgent.match(/iPod/i)
  //     || navigator.userAgent.match(/BlackBerry/i)
  //     || navigator.userAgent.match(/Windows Phone/i)
  //   ) {
  //     console.log('é mobile ', true) //celular
  //   }
  //   else {
  //     console.log('não é mobile: ', false) //nao é celular
  //   }
  // }, []);
  //##############################################################################

  const socketManager = useContext(SocketContext);



  useEffect(() => {
    if (isMobileWidth()) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  // O padrao da empresa so vale para quem nunca alternou a barra: a escolha do
  // usuario, uma vez feita, sobrevive a mudancas da configuracao da empresa.
  useEffect(() => {
    if (isMobileWidth()) return;
    if (localStorage.getItem(userPrefKey(companyId))) return;
    setDrawerOpen(sidebarBehavior === "expanded");
  }, [sidebarBehavior, companyId]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const userId = localStorage.getItem("userId");

    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-auth`, (data) => {
      if (data.user.id === +userId) {
        if (data.action === "force-logout") {
          // Mostrar mensagem personalizada se fornecida
          const message = data.message || "Sua conta foi acessada em outro computador.";
          // Licença Empsis bloqueada: o motivo vai para o aviso fixo da tela de
          // login, que sobrevive ao recarregamento (o toast não sobrevive).
          if (data.reason === "license") {
            setLicenseNotice(message);
          } else {
            toastError(message);
          }
          
          // Executar logout imediatamente
          setTimeout(() => {
            clearSessionKeys();
            // Desconectar socket antes de recarregar
            socket.disconnect();
            window.location.href = "/login";
          }, 1000);
        } else if (data.action === "new-login") {
          // Confirmar que este é o novo login autorizado
          console.log("Nova sessão autorizada para o usuário:", data.user.id);
        }
      }
    });

    // Havia aqui um segundo fetch de /auth/session-config que montava um
    // setInterval de 1 minuto cujo corpo inteiro era um console.warn
    // ("Token expirado localmente, verificando com backend antes de deslogar").
    // Não validava nem renovava nada — só poluía o console e dava a impressão de
    // que alguém estava cuidando do token. Quem renova é o interceptor do
    // services/api.js, junto com a renovação periódica do useAuth.

    socket.emit("userStatus");
    const interval = setInterval(() => {
      socket.emit("userStatus");
    }, 1000 * 60 * 5);

    return () => {
      socket.disconnect();
      clearInterval(interval);
    };
  }, [socketManager]);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  // Alternar pelo chevron/hamburguer registra a preferencia do usuario, que
  // passa a ter precedencia sobre o padrao da empresa. No mobile a barra e um
  // overlay que sempre abre fechado, entao abrir/fechar ali nao e preferencia.
  const toggleDrawer = () => {
    setDrawerOpen((prev) => {
      const next = !prev;
      if (drawerVariant === "permanent") {
        localStorage.setItem(userPrefKey(companyId), next ? "open" : "closed");
      }
      return next;
    });
  };

  // So faz sentido fechar ao clicar fora quando a barra e um overlay temporario
  // (mobile). No modo permanente isso desfazia a configuracao da empresa.
  const maybeCloseOnClick = () => {
    if (drawerVariant !== "permanent" && drawerOpen) {
      setDrawerOpen(false);
    }
  };

  const handleRefreshPage = () => {
    window.location.reload(false);
  }

  const handleClearCache = async () => {
    setClearingCache(true);
    await clearAppData();
  };

  // Outra aba limpou os dados do CRM: esta recarrega no /login para não seguir
  // com a versão antiga na memória.
  useEffect(() => {
    if (typeof BroadcastChannel !== "function") return undefined;
    const channel = new BroadcastChannel(CLEAR_APP_DATA_CHANNEL);
    channel.onmessage = () => window.location.replace("/login");
    return () => channel.close();
  }, []);

  const handleOpenFollowupPopupShortcut = () => {
    // Autor: GitHub Copilot | Data: 23/03/2026 | Motivo: criar atalho no topo para abrir popup de follow-up sem depender do relogin.
    const opened = openFollowupDashboardPopup();
    if (!opened) {
      toastError(i18n.t("mainDrawer.appBar.toasts.popupBlocked"));
    }
  };

  const handleMenuItemClick = () => {
    const { innerWidth: width } = window;
    if (width <= 960) {
      setDrawerOpen(false);
    }
  };

  const toggleColorMode = () => {
    colorMode.toggleColorMode();
  }

  // Se não está autenticado, não renderiza o layout (deixa as rotas públicas funcionarem)
  if (!isAuth) {
    return null;
  }

  if (loading) {
    return <BackdropLoading />;
  }

  return (
    <div className={classes.root}>
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        ModalProps={{
          keepMounted: true, // Melhor performance em mobile
        }}
      >
        <div className={classes.toolbarIcon}>
          <img src={logo} className={classes.logo} alt="logo" />
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List className={classes.containerWithScroll}>
          <MainListItems
            collapsed={!drawerOpen}
            onItemClick={maybeCloseOnClick}
            sidebarBehavior={sidebarBehavior}
            sidebarSubmenuBehavior={sidebarSubmenuBehavior}
            drawerVariant={drawerVariant}
          />
        </List>
        <Divider />
        {/* Fora da List (que tem flex:1), a versao encosta no rodape do drawer
            mesmo quando o menu nao preenche a tela inteira. */}
        {drawerOpen && (
          <Typography className={classes.drawerVersion}>{`6.0.0`}</Typography>
        )}
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <ConfirmationModal
        title={i18n.t("mainDrawer.appBar.clearCache.title")}
        open={clearCacheModalOpen}
        onClose={() => setClearCacheModalOpen(false)}
        onConfirm={handleClearCache}
      >
        {i18n.t("mainDrawer.appBar.clearCache.message")}
      </ConfirmationModal>
      {clearingCache && <BackdropLoading />}
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
        color="primary"
        onClick={maybeCloseOnClick}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            variant="contained"
            aria-label="open drawer"
            onClick={(e) => {
              e.stopPropagation();
              toggleDrawer();
            }}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            component="h2"
            variant="h6"
            color="inherit"
            noWrap
            className={classes.title}
          >
            {/* {greaterThenSm && user?.profile === "admin" && getDateAndDifDays(user?.company?.dueDate).difData < 7 ? ( */}
            {greaterThenSm && user?.profile === "admin" && user?.company?.dueDate ? (
              <>
                {i18n.t("mainDrawer.appBar.greeting.hello")} <b>{user.name}</b>, {i18n.t("mainDrawer.appBar.greeting.welcome")} <b>{user?.company?.name}</b>! ({i18n.t("mainDrawer.appBar.greeting.activeUntil", { date: dateToClient(user?.company?.dueDate) })})
              </>
            ) : (
              <>
                {i18n.t("mainDrawer.appBar.greeting.hello")} <b>{user.name}</b>, {i18n.t("mainDrawer.appBar.greeting.welcome")} <b>{user?.company?.name}</b>!
              </>
            )}
            <SessionStatusIndicator />
          </Typography>

          <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.darkMode")}>
            <IconButton edge="start" onClick={toggleColorMode} aria-label={i18n.t("mainDrawer.appBar.tooltips.darkMode")}>
              {theme.mode === 'dark' ? <Brightness7Icon style={{ color: "white" }} /> : <Brightness4Icon style={{ color: "white" }} />}
            </IconButton>
          </Tooltip>

          <NotificationsVolume
            setVolume={setVolume}
            volume={volume}
          />

          <Tooltip title={i18n.t("mainDrawer.appBar.refresh")}>
            <IconButton
              onClick={handleRefreshPage}
              aria-label={i18n.t("mainDrawer.appBar.refresh")}
              color="inherit"
            >
              <CachedIcon style={{ color: "white" }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={i18n.t("mainDrawer.appBar.clearCache.tooltip")}>
            <IconButton
              onClick={() => setClearCacheModalOpen(true)}
              aria-label={i18n.t("mainDrawer.appBar.clearCache.tooltip")}
              color="inherit"
            >
              <DeleteSweepIcon style={{ color: "white" }} />
            </IconButton>
          </Tooltip>

          <Tooltip title={i18n.t("mainDrawer.appBar.openFollowUp")}>
            <IconButton
              onClick={handleOpenFollowupPopupShortcut}
              aria-label={i18n.t("mainDrawer.appBar.openFollowUp")}
              color="inherit"
            >
              <EventNoteIcon style={{ color: "white" }} />
            </IconButton>
          </Tooltip>

          {user.id && <NotificationsPopOver volume={volume} />}

          <AnnouncementsPopover />

          <ChatPopover volume={volume} />

          <div>
            <Tooltip title={i18n.t("mainDrawer.appBar.tooltips.account")}>
              <IconButton
                aria-label={i18n.t("mainDrawer.appBar.tooltips.account")}
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                variant="contained"
                style={{ color: "white" }}
              >
                <AccountCircle />
              </IconButton>
            </Tooltip>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <LanguageMenu onSelected={handleCloseMenu} />
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <main className={classes.content} onClick={maybeCloseOnClick}>
        <div className={classes.appBarSpacer} />

        {children ? children : null}
      </main>
      
      {/* Avisa por toast quando uma conexão que o usuário usa cai */}
      <ConnectionAlerts />

      {/* Ligação em andamento / recebida: fica fora do <main> para seguir na tela ao navegar */}
      <CallPanel />

      {/* Menu de Debug - apenas para usuário ID 1 */}
      <DebugMenu />
    </div>
  );
};

export default LoggedInLayout;
